import { useState, useCallback, useRef, useImperativeHandle, forwardRef, useContext, memo, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    Box, SimpleGrid, Button, useBreakpointValue,
    AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
} from '@chakra-ui/react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
    type Modifier,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { WidgetConfig, WidgetEditorCatalog, WidgetGridSize } from './types';
import {
    RANKING_METRICS, SUMMARY_METRICS, TIMESERIES_VOLUME_METRICS, TIMESERIES_MARGIN_METRICS,
    STANDARD_CHART_HEIGHT, WIDGET_GRID_GAP_PX, clampGridSpan, clampWidgetHeight,
} from './types';
import {
    assignChartSlots,
    chartGridPlacement,
    originFromDragDelta,
    widgetsAfterMoveChart,
    widgetsAfterResizeChart,
} from './widgetDashboardLayout';
import type { AuditingDrilldownPayload } from './WidgetRenderer';
import { getWidgetExportData, useWidgetData, type WidgetSharedParams } from './useWidgetData';
import WidgetCard from './WidgetCard';
import WidgetRenderer from './WidgetRenderer';
import WidgetEditDrawer from './WidgetEditDrawer';
import { exportToCsv } from '../../../utils/csvExport';
import SessionContext from '../../../contexts/SessionContext';

const DRAG_ACTIVATE_PX = 8;

const ALL_METRICS = [...SUMMARY_METRICS, ...TIMESERIES_VOLUME_METRICS, ...TIMESERIES_MARGIN_METRICS, ...RANKING_METRICS];
function getMetricLabel(metric: string): string {
    return ALL_METRICS.find(m => m.value === metric)?.label || metric;
}
function getWidgetDisplayTitle(w: WidgetConfig): string {
    if (w.widgetType === 'keyMetric') return w.title;
    const metricLabel = getMetricLabel(w.metric);
    if (metricLabel === w.metric) return w.title;
    if (w.title.toLowerCase().includes(metricLabel.toLowerCase())) return w.title;
    return `${w.title}: ${metricLabel}`;
}

interface OverviewWidgetCardProps {
    widget: WidgetConfig;
    params: WidgetSharedParams;
    isRefetching?: boolean;
    children: ReactNode;
    title: string;
    description?: string;
    isKeyMetric?: boolean;
    onEdit: () => void;
    onDownload: () => void;
    onDelete: () => void;
    dragHandleProps?: Record<string, unknown>;
    currentSpan?: number;
    currentHeight?: number;
    currentGridX?: number;
    currentGridY?: number;
    onMovePointerDown?: (event: React.PointerEvent) => void;
    isMoving?: boolean;
    onResize?: (next: WidgetGridSize) => void;
    onResizePreview?: (next: WidgetGridSize | null) => void;
    noPadding?: boolean;
}

/** Wraps WidgetCard with refetch state from useWidgetData (Overview dashboard only). */
const OverviewWidgetCard = memo(function OverviewWidgetCard({
    widget,
    params,
    isRefetching: isRefetchingOverride,
    children,
    ...cardProps
}: OverviewWidgetCardProps) {
    const widgetState = useWidgetData(widget, params);
    const isRefetching = isRefetchingOverride ?? widgetState.isRefetching;
    return (
        <WidgetCard
            {...cardProps}
            isRefetching={isRefetching}
        >
            {children}
        </WidgetCard>
    );
});

interface SortableWidgetProps {
    id: string;
    minW?: number | string;
    h?: string;
    gridColumn?: string;
    gridRow?: string;
    children: (props: {
        dragHandleListeners: any;
        dragHandleAttributes: Record<string, any>;
        isDragging: boolean;
    }) => React.ReactNode;
}

const SortableWidget = ({ id, children, minW, h, gridColumn, gridRow }: SortableWidgetProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
    };

    return (
        <Box ref={setNodeRef} style={style} minW={minW} h={h} minH={0} gridColumn={gridColumn} gridRow={gridRow}>
            {children({
                dragHandleListeners: listeners,
                dragHandleAttributes: attributes,
                isDragging,
            })}
        </Box>
    );
};

export interface WidgetGridHandle {
    openAddWidget: () => void;
}

interface WidgetGridProps {
    widgets: WidgetConfig[];
    params: WidgetSharedParams;
    onUpdate: (id: string, updates: Partial<WidgetConfig>) => void;
    onRemove: (id: string) => void;
    onAdd: (widget: WidgetConfig) => void;
    onReorder: (widgets: WidgetConfig[]) => void;
    onAuditingDrilldown?: (payload: AuditingDrilldownPayload) => void;
    isGm?: boolean;
    /** When provided, renders each widget with this function instead of the default order WidgetRenderer. */
    renderWidget?: (config: WidgetConfig) => React.ReactNode;
    /** Parameterizes the edit drawer for non-overview dashboards. */
    editorCatalog?: WidgetEditorCatalog;
    /** Optional custom CSV download per widget (used when renderWidget is provided). */
    onDownloadWidget?: (config: WidgetConfig) => void;
    /** Refetch with existing data — fade + header spinner (e.g. Invoicing). */
    getWidgetRefetching?: (config: WidgetConfig) => boolean;
}

const WidgetGrid = forwardRef<WidgetGridHandle, WidgetGridProps>(({ widgets, params, onUpdate, onRemove, onAdd, onReorder, onAuditingDrilldown, isGm = false, renderWidget, editorCatalog, onDownloadWidget, getWidgetRefetching }, ref) => {
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const [editingWidget, setEditingWidget] = useState<WidgetConfig | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [layoutPreview, setLayoutPreview] = useState<WidgetConfig[] | null>(null);
    const [movingId, setMovingId] = useState<string | null>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);
    const moveCleanupRef = useRef<(() => void) | null>(null);
    const kpiGridRef = useRef<HTMLDivElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: DRAG_ACTIVATE_PX } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    useEffect(() => {
        return () => { moveCleanupRef.current?.(); };
    }, []);

    const displayWidgets = layoutPreview ?? widgets;
    const kpiWidgets = displayWidgets.filter(w => w.widgetType === 'keyMetric');
    const chartWidgets = assignChartSlots(displayWidgets.filter(w => w.widgetType !== 'keyMetric'));
    const kpiIds = kpiWidgets.map(w => w.id);
    const kpiColumnCount = useBreakpointValue({
        base: 1,
        md: 2,
        lg: 3,
        xl: Math.min(kpiWidgets.length, 5),
    }) ?? 1;
    const lockKpiAxis: Modifier = ({ transform, activeNodeRect, draggingNodeRect }) => {
        let next = transform;
        if (kpiWidgets.length <= 1) next = { ...next, x: 0, y: 0 };
        else if (kpiColumnCount <= 1) next = { ...next, x: 0 };
        else if (kpiWidgets.length <= kpiColumnCount) next = { ...next, y: 0 };

        const node = activeNodeRect ?? draggingNodeRect;
        const grid = kpiGridRef.current?.getBoundingClientRect();
        if (!node || !grid) return next;

        let { x, y } = next;
        if (node.top + y < grid.top) y = grid.top - node.top;
        if (node.bottom + y > grid.bottom) y = grid.bottom - node.bottom;
        if (node.left + x < grid.left) x = grid.left - node.left;
        if (node.right + x > grid.right) x = grid.right - node.right;
        return { ...next, x, y };
    };

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const activeId = String(active.id);
        const overId = String(over.id);

        const kpis = widgets.filter(w => w.widgetType === 'keyMetric');
        const charts = widgets.filter(w => w.widgetType !== 'keyMetric');
        if (!kpis.some(w => w.id === activeId) || !kpis.some(w => w.id === overId)) return;

        const oldIndex = kpis.findIndex(w => w.id === activeId);
        const newIndex = kpis.findIndex(w => w.id === overId);
        if (oldIndex === -1 || newIndex === -1) return;

        onReorder([...arrayMove(kpis, oldIndex, newIndex), ...charts]);
    }, [widgets, onReorder]);

    const handleAddNew = useCallback(() => {
        setEditingWidget(null);
        setIsCreating(true);
    }, []);

    useImperativeHandle(ref, () => ({ openAddWidget: handleAddNew }));

    const handleEdit = useCallback((widget: WidgetConfig) => {
        setEditingWidget(widget);
        setIsCreating(false);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setEditingWidget(null);
        setIsCreating(false);
    }, []);

    const handleDrawerSave = useCallback((config: WidgetConfig) => {
        if (editingWidget) {
            onUpdate(editingWidget.id, config);
        } else {
            onAdd(config);
        }
        handleDrawerClose();
    }, [editingWidget, onUpdate, onAdd, handleDrawerClose]);

    const handleResize = useCallback((widget: WidgetConfig, next: WidgetGridSize) => {
        onReorder(widgetsAfterResizeChart(widgets, widget.id, next));
    }, [widgets, onReorder]);

    const handleResizePreview = useCallback((widgetId: string, next: WidgetGridSize | null) => {
        setLayoutPreview(next ? widgetsAfterResizeChart(widgets, widgetId, next) : null);
    }, [widgets]);

    const handleMovePointerDown = useCallback((widgetId: string, event: React.PointerEvent) => {
        event.stopPropagation();
        const grid = (event.currentTarget as HTMLElement).closest('[data-widget-grid]');
        const moving = widgets.find((widget) => widget.id === widgetId);
        if (!grid || !moving) return;

        const startClient = { x: event.clientX, y: event.clientY };
        const start = { gridX: moving.gridX ?? 0, gridY: moving.gridY ?? 0 };
        const width = clampGridSpan(moving.gridSpan);
        const height = clampWidgetHeight(moving.widgetHeight);
        let lastDest: { gridX: number; gridY: number } | null = null;
        let isHolding = false;

        const destAt = (x: number, y: number) => originFromDragDelta(
            start,
            startClient,
            { x, y },
            grid.getBoundingClientRect().width,
            width,
            height,
        );

        const onMove = (moveEvent: PointerEvent) => {
            if (!isHolding) {
                const dx = moveEvent.clientX - startClient.x;
                const dy = moveEvent.clientY - startClient.y;
                if (dx * dx + dy * dy < DRAG_ACTIVATE_PX * DRAG_ACTIVATE_PX) return;
                isHolding = true;
                setMovingId(widgetId);
                document.body.style.cursor = 'grabbing';
                document.body.style.userSelect = 'none';
            }

            const dest = destAt(moveEvent.clientX, moveEvent.clientY);
            if (dest.gridX === start.gridX && dest.gridY === start.gridY) {
                if (lastDest) {
                    lastDest = null;
                    setLayoutPreview(null);
                }
                return;
            }
            if (lastDest?.gridX === dest.gridX && lastDest?.gridY === dest.gridY) return;
            lastDest = dest;
            setLayoutPreview(widgetsAfterMoveChart(widgets, widgetId, dest.gridX, dest.gridY));
        };
        const cleanup = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            moveCleanupRef.current = null;
        };
        const onUp = (upEvent: PointerEvent) => {
            cleanup();
            const dest = destAt(upEvent.clientX, upEvent.clientY);
            if (isHolding && (dest.gridX !== start.gridX || dest.gridY !== start.gridY)) {
                onReorder(widgetsAfterMoveChart(widgets, widgetId, dest.gridX, dest.gridY));
            }
            setLayoutPreview(null);
            setMovingId(null);
        };
        const onCancel = () => {
            cleanup();
            setLayoutPreview(null);
            setMovingId(null);
        };

        moveCleanupRef.current = cleanup;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
    }, [widgets, onReorder]);

    const handleDownload = useCallback(async (widget: WidgetConfig) => {
        if (onDownloadWidget) {
            onDownloadWidget(widget);
            return;
        }
        const exportData = await getWidgetExportData(
            queryClient,
            sessionContext?.sessionId ?? '',
            widget,
            params,
        );
        if (exportData) {
            exportToCsv(exportData, widget.title.replace(/\s+/g, '_').toLowerCase());
        }
    }, [params, queryClient, sessionContext?.sessionId, onDownloadWidget]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            autoScroll={false}
            modifiers={[lockKpiAxis]}
            onDragEnd={handleDragEnd}
        >
            {kpiWidgets.length > 0 && (
                <SortableContext items={kpiIds} strategy={rectSortingStrategy}>
                    <SimpleGrid
                        ref={kpiGridRef}
                        columns={{ base: 1, md: 2, lg: 3, xl: Math.min(kpiWidgets.length, 5) }}
                        spacing={4}
                        mb={4}
                        overflowX="hidden"
                    >
                        {kpiWidgets.map(widget => (
                            <SortableWidget key={widget.id} id={widget.id}>
                                {({ dragHandleListeners, dragHandleAttributes }) => (
                                    renderWidget ? (
                                        <WidgetCard
                                            title={widget.title}
                                            description={widget.description}
                                            isKeyMetric
                                            isRefetching={getWidgetRefetching?.(widget)}
                                            onEdit={() => handleEdit(widget)}
                                            onDownload={() => handleDownload(widget)}
                                            onDelete={() => setDeletingId(widget.id)}
                                            dragHandleProps={{ ...dragHandleListeners, ...dragHandleAttributes }}
                                        >
                                            {renderWidget(widget)}
                                        </WidgetCard>
                                    ) : (
                                        <OverviewWidgetCard
                                            widget={widget}
                                            params={params}
                                            title={widget.title}
                                            description={widget.description}
                                            isKeyMetric
                                            onEdit={() => handleEdit(widget)}
                                            onDownload={() => handleDownload(widget)}
                                            onDelete={() => setDeletingId(widget.id)}
                                            dragHandleProps={{ ...dragHandleListeners, ...dragHandleAttributes }}
                                        >
                                            <WidgetRenderer config={widget} params={params} />
                                        </OverviewWidgetCard>
                                    )
                                )}
                            </SortableWidget>
                        ))}
                    </SimpleGrid>
                </SortableContext>
            )}

            {chartWidgets.length > 0 && (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                    gridAutoRows={`${STANDARD_CHART_HEIGHT}px`}
                    gap={`${WIDGET_GRID_GAP_PX}px`}
                    data-widget-grid
                >
                    {chartWidgets.map(widget => {
                        const placement = chartGridPlacement(widget);
                        const resizeProps = {
                            currentSpan: clampGridSpan(widget.gridSpan),
                            currentHeight: clampWidgetHeight(widget.widgetHeight),
                            currentGridX: widget.gridX ?? 0,
                            currentGridY: widget.gridY ?? 0,
                            onResize: (next: WidgetGridSize) => handleResize(widget, next),
                            onResizePreview: (next: WidgetGridSize | null) => handleResizePreview(widget.id, next),
                            onMovePointerDown: (event: React.PointerEvent) => handleMovePointerDown(widget.id, event),
                            isMoving: movingId === widget.id,
                        };
                        return (
                            <Box
                                key={widget.id}
                                minW={0}
                                minH={0}
                                h="100%"
                                gridColumn={placement.gridColumn}
                                gridRow={placement.gridRow}
                            >
                                {renderWidget ? (
                                    <WidgetCard
                                        title={getWidgetDisplayTitle(widget)}
                                        description={widget.description}
                                        isRefetching={getWidgetRefetching?.(widget)}
                                        onEdit={() => handleEdit(widget)}
                                        onDownload={() => handleDownload(widget)}
                                        onDelete={() => setDeletingId(widget.id)}
                                        noPadding={widget.widgetType === 'table'}
                                        {...resizeProps}
                                    >
                                        {renderWidget(widget)}
                                    </WidgetCard>
                                ) : (
                                    <OverviewWidgetCard
                                        widget={widget}
                                        params={params}
                                        title={getWidgetDisplayTitle(widget)}
                                        description={widget.description}
                                        onEdit={() => handleEdit(widget)}
                                        onDownload={() => handleDownload(widget)}
                                        onDelete={() => setDeletingId(widget.id)}
                                        noPadding={widget.widgetType === 'table'}
                                        {...resizeProps}
                                    >
                                        <WidgetRenderer
                                            config={widget}
                                            params={params}
                                            onAuditingDrilldown={onAuditingDrilldown}
                                        />
                                    </OverviewWidgetCard>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            <WidgetEditDrawer
                isOpen={!!editingWidget || isCreating}
                onClose={handleDrawerClose}
                onSave={handleDrawerSave}
                initialConfig={editingWidget || undefined}
                isCreating={isCreating}
                isGm={isGm}
                preset={params.preset}
                catalog={editorCatalog}
            />

            <AlertDialog
                isOpen={!!deletingId}
                leastDestructiveRef={cancelRef as any}
                onClose={() => setDeletingId(null)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Widget</AlertDialogHeader>
                        <AlertDialogBody>Are you sure? This will remove the widget from your dashboard.</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button size="action" variant="outlineGray" ref={cancelRef as any} onClick={() => setDeletingId(null)}>Cancel</Button>
                            <Button
                                size="action"
                                colorScheme="red"
                                onClick={() => { if (deletingId) { onRemove(deletingId); setDeletingId(null); } }}
                                ml={3}
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </DndContext >
    );
});

WidgetGrid.displayName = 'WidgetGrid';
export default WidgetGrid;
